import dayjs from 'dayjs';
import _ from 'lodash';
import 'dayjs/locale/fr';
import { Button, Dropdown, Header, Loader, Message, Modal, Segment } from '@mairistem/composants';
import { Liste, ListeTable, ListeTableActionCsv, ListeTableActionPrint } from '@mairistem/liste';

import { request } from '@mairistem/tools';
import React, { type ReactNode } from 'react';
import { toast } from 'react-toastify';
import { ARRETES } from '@/constants';
import useListeSearchSort from '@/hooks/useListeSearchSort';
import { getArretes, postArrete, searchArretes } from '@/routes';
import type { ArreteType } from '@/types';
import { formatMonetaire } from '@/utils';
import { getHttpStatus } from '@/utils/apiErrors';
import { useRegieStore } from '@/zustand';
import ArretesJournaliersListeTableHeader from './ArretesJournaliersListeTableHeader';
import ArretesJournaliersListeTableRow from './ArretesJournaliersListeTableRow';

dayjs.locale('fr');

const FILTRE_DERNIER_ARRETE = 'dernierArrete';
const FILTRE_ANNEE = 'annee';

const filtreOptions = [
  {
    key: FILTRE_DERNIER_ARRETE,
    value: FILTRE_DERNIER_ARRETE,
    text: 'Depuis le dernier arrêté mensuel de la régie',
  },
  {
    key: FILTRE_ANNEE,
    value: FILTRE_ANNEE,
    text: "Tous les arrêtés journaliers de l'année",
  },
];

const ArretesJournaliers = () => {
  const regie = useRegieStore((state) => state.regie);
  const [filtrePeriode, setFiltrePeriode] = React.useState(FILTRE_DERNIER_ARRETE);
  const [creationLoading, setCreationLoading] = React.useState(true);
  const [arreteCreated, setArreteCreated] = React.useState<ArreteType | null>(null);

  const isDernierArrete = filtrePeriode === FILTRE_DERNIER_ARRETE;
  const perPage = isDernierArrete ? 365 : 15;

  const fixedParams = React.useMemo(() => {
    const params: Record<string, any> = { type: ARRETES.TYPE_JOURNALIER };

    if (isDernierArrete) {
      params.depuis = 'dernierArrete';
      params.perPage = 365;
    } else {
      params.date = `since(${dayjs().year()}-01-01)`;
    }

    return params;
  }, [isDernierArrete]);

  const { items, loading, total, search, setSorts, setPage, setSearch, reset } = useListeSearchSort(
    {
      active: !_.isNil(regie?.identifiant) && !creationLoading,
      getItems: getArretes,
      searchItems: searchArretes,
      fixedParams,
      all: true,
    },
  );

  const handleFiltrePeriodeChange = React.useCallback((_e, { value }) => {
    setFiltrePeriode(value);
  }, []);

  // Recharger la liste quand le filtre change (après le premier rendu)
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    reset();
  }, [filtrePeriode]);

  // Polling 30 s tant que le dernier arrêté n'a pas ses 2 éditions auto-générées
  // (jobs backend asynchrones). S'arrête seul dès que les documents sont présents.
  const hasMissingDocuments = React.useMemo(() => {
    const latest = _.maxBy(items as ArreteType[] | undefined, 'date');
    if (!latest) return false;
    const docs = latest.documents ?? [];
    const hasProduitMr = docs.some((d) => d.categorie === ARRETES.CATEGORIE_RECAPITULATIF_MR_JR);
    const hasParRecu = docs.some((d) => d.categorie === ARRETES.CATEGORIE_RECAPITULATIF_PAR_RECU);
    return !hasProduitMr || !hasParRecu;
  }, [items]);

  React.useEffect(() => {
    if (!hasMissingDocuments) return undefined;
    const intervalId = setInterval(() => reset(1), 30_000);
    return () => clearInterval(intervalId);
  }, [hasMissingDocuments, reset]);

  // Création automatique d'un arrêté journalier à chaque arrivée sur la page
  React.useEffect(() => {
    let cancelled = false;
    setCreationLoading(true);

    (async () => {
      try {
        const response: any = await request.post(postArrete(), {
          type: ARRETES.TYPE_JOURNALIER,
        });

        if (cancelled) return;

        const arrete = response?.data || response;

        if (arrete?.identifiant) {
          setArreteCreated(arrete);
        }
      } catch (error: any) {
        if (!cancelled && getHttpStatus(error) !== 422) {
          toast.error("Une erreur est survenue lors de la création de l'arrêté journalier");
        }
      } finally {
        if (!cancelled) {
          setCreationLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSort = React.useCallback(
    (sort) => {
      setSorts(
        _.isEmpty(sort) ? null : _.mapValues(sort, (val) => (val === 'ascending' ? 'asc' : 'desc')),
      );
    },
    [setSorts],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  const handleSearch = React.useCallback(
    (value: string) => {
      setPage(1);
      setSearch(value);
    },
    [setPage, setSearch],
  );

  const renderRow = (item: ArreteType, _invalidate: unknown, rowProps: Record<string, unknown>) => (
    <ArretesJournaliersListeTableRow item={item} rowProps={rowProps} />
  );

  const arreteCreatedMessage = React.useMemo<React.ReactNode>(() => {
    if (!arreteCreated) return null;

    const date = dayjs(arreteCreated.date).locale('fr').format('D MMMM YYYY');
    const { montantTotal } = arreteCreated;
    const sansEncaissement = _.isNil(montantTotal) || montantTotal === 0;

    if (sansEncaissement) {
      const montantAnnulation = formatMonetaire(arreteCreated.montantTotalAnnulation ?? 0, 2, true);

      return (
        <>
          {'La journée du '}
          <strong>{date}</strong>
          {' est arrêtée sans encaissement.'}
          <br />
          {'Des annulations de reçus ont été saisies pour un montant de '}
          <strong>{montantAnnulation}</strong>.
        </>
      );
    }

    const montant = formatMonetaire(montantTotal, 2, true);
    const heure = dayjs(arreteCreated.date).format('HH[h]mm');

    return (
      <>
        {'À '}
        <strong>{heure}</strong>
        {", sous réserve d'encaissements ou annulations ultérieurs, la journée du "}
        <strong>{date}</strong>
        {' est arrêtée pour la somme de '}
        <strong>{montant}</strong>.
      </>
    );
  }, [arreteCreated]);

  if (creationLoading) {
    return (
      <Segment
        basic
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Loader active size="large" content="Création de l'arrêté journalier..." />
      </Segment>
    );
  }

  return (
    <>
      {arreteCreated && (
        <Modal open onClose={() => setArreteCreated(null)} closeIcon>
          <Modal.Header>Arrêté journalier</Modal.Header>
          <Modal.Content>
            <Message info>
              <Message.Header>{arreteCreatedMessage}</Message.Header>
            </Message>
          </Modal.Content>
          <Modal.Actions>
            <Button content="OK" onClick={() => setArreteCreated(null)} positive />
          </Modal.Actions>
        </Modal>
      )}
      <Segment
        inverted
        secondary
        style={{
          flex: 1,
          overflow: 'auto',
          background: 'transparent',
        }}
      >
        <Segment style={{ borderRadius: 14 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Header as="h1" style={{ marginBottom: '1em' }} textAlign="center">
              <Header.Content>Arrêtés journaliers</Header.Content>
            </Header>
            <Liste
              title=""
              // @ts-expect-error
              onSearch={handleSearch}
              loading={loading}
              searchable
              search={{
                value: search,
                placeholder: 'Rechercher un arrêté journalier...',
                clearable: true,
                autoFocus: true,
              }}
            >
              <Liste.Header>
                <Dropdown
                  selection
                  options={filtreOptions}
                  value={filtrePeriode}
                  onChange={handleFiltrePeriodeChange}
                  style={{ width: 350, marginTop: '-1em' }}
                />
              </Liste.Header>
              <Liste.Content>
                <div style={{ height: 450 }}>
                  <ListeTable
                    count={_.isEmpty(items) ? 0 : total}
                    loading={loading}
                    items={items as ArreteType[]}
                    onSort={handleSort}
                    onLoad={handleLoad}
                    perPage={perPage}
                    noResultsIcon=""
                    noResultsMessage="Aucun arrêté journalier trouvé pour cette régie"
                  >
                    <ListeTable.Header>
                      <ArretesJournaliersListeTableHeader />
                    </ListeTable.Header>
                    {renderRow as unknown as ReactNode}
                    {isDernierArrete && (
                      <ListeTable.Actions>
                        <ListeTableActionCsv
                          items={items as ArreteType[]}
                          columns={['libelle', 'date', 'heure', 'montantTotal']}
                          options={{ filename: 'Arrêtés journaliers' }}
                        />
                        <ListeTableActionPrint
                          printOptions={{
                            documentName: 'Arrêtés journaliers',
                            pageStyle: '[data-name="document"] { display: none !important; }',
                          }}
                        />
                      </ListeTable.Actions>
                    )}
                  </ListeTable>
                </div>
              </Liste.Content>
            </Liste>
          </div>
        </Segment>
      </Segment>
    </>
  );
};

export default ArretesJournaliers;
